import { useState } from "react";
import { toast } from "sonner";
import { Gavel } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Controlled ban-user dialog. Reusable from anywhere staff sees a user's
 * name (reviews, reports, comments, etc.).
 *
 * Props:
 *   open, onOpenChange — controlled modal state
 *   target             — { user_id, name }
 *   defaultPurgeReviews (bool, default true)
 *   onBanned(banRes)   — fired after a successful ban
 */
export default function BanUserDialog({
  open,
  onOpenChange,
  target,
  defaultPurgeReviews = true,
  onBanned,
}) {
  const [reason, setReason] = useState("");
  const [purgeReviews, setPurgeReviews] = useState(defaultPurgeReviews);
  const [purgeReports, setPurgeReports] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setReason("");
    setPurgeReviews(defaultPurgeReviews);
    setPurgeReports(false);
    setBusy(false);
  };

  const submit = async () => {
    if (!target) return;
    if (!reason.trim()) return toast.error("Enter a reason");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/ban", {
        user_id: target.user_id,
        reason: reason.trim(),
        purge_reviews: purgeReviews,
        purge_reports: purgeReports,
      });
      const bits = [];
      if (data?.purged?.reviews) bits.push(`${data.purged.reviews} reviews`);
      if (data?.purged?.reports) bits.push(`${data.purged.reports} reports`);
      toast.success(
        `Banned ${target.name}${bits.length ? ` · wiped ${bits.join(" + ")}` : ""}`
      );
      onBanned?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ban failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="bg-[#0A0A0F] border-white/10 text-white max-w-md"
        data-testid="ban-user-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading inline-flex items-center gap-2">
            <Gavel className="w-4 h-4 text-purple-300" /> Ban {target?.name || "user"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            They&apos;ll be logged out and blocked from logging in, submitting servers, reviewing, or reporting.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="Reason (required) — e.g. review-bombing across multiple listings."
            data-testid="ban-user-reason"
            className="w-full px-3 py-2 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-purple-400 focus:outline-none resize-y"
          />

          <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={purgeReviews}
              onChange={(e) => setPurgeReviews(e.target.checked)}
              data-testid="ban-purge-reviews"
              className="mt-0.5 accent-purple-500"
            />
            <span>
              <span className="font-semibold">Delete all their reviews</span>
              <span className="block text-xs text-zinc-500">
                Auto-recomputes each affected server&apos;s rating.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={purgeReports}
              onChange={(e) => setPurgeReports(e.target.checked)}
              data-testid="ban-purge-reports"
              className="mt-0.5 accent-purple-500"
            />
            <span>
              <span className="font-semibold">Delete all their reports</span>
              <span className="block text-xs text-zinc-500">
                Wipes any pending reports they filed.
              </span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 px-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !reason.trim()}
            data-testid="ban-user-confirm"
            className="h-9 px-4 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Gavel className="w-4 h-4" /> {busy ? "Banning…" : "Confirm ban"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
